import { useState } from "react";
import { Link } from "react-router";
import classNames from "classnames";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ENDPOINTS } from "@common/utils/url-registry";
import {
    AvimarkBridgeUnreachableError,
    AvimarkSetupAlreadyRunningError,
    startAvimarkCalibration,
} from "@common/lib/api/avimarkCalibration";
import { bridgeStatusQueryKey, fetchBridgeStatus } from "@common/lib/api/bridge";
import { LOST_CONTACT_AFTER_MINUTES, minutesSince } from "@/state/hooks/useBridgeSetup";
import { SUPPORT_EMAIL } from "@/common/lib/supportHours";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useAvimarkCalibration, avimarkCalibrationKey } from "../hooks/useAvimarkCalibration";
import { describeExamSetup, type ExamSetupTone } from "../examSetupStatus";
import { reportAvimarkError } from "../reportAvimarkError";

const TONE: Record<ExamSetupTone, string> = {
    ok: "bg-max-50 border-max-200 text-max-950",
    warn: "bg-tropicana-50 border-tropicana-300 text-tropicana-950",
    bad: "bg-red-50 border-red-200 text-red-900",
    muted: "bg-gray-50 border-gray-200 text-gray-700",
};
const SETUP_PAGE = `/${ENDPOINTS.AvimarkExamSetup}`;
const SUPPORT_URL = `mailto:${SUPPORT_EMAIL}`;

/** "YYYY-MM-DD" with no UTC shift: new Date("2025-12-22") would show 12/21 in US time zones. */
function formatDateOnly(value: string | null | undefined): string | null {
    const m = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    if (!m) return value ? new Date(value).toLocaleDateString() : null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
}

/** EN-1249 F10-F16 / F12: "AVImark exam setup" block of the hub. */
export default function ExamSetupBlock() {
    const { accountData } = useAccountContext();
    const qc = useQueryClient();
    const cal = useAvimarkCalibration(true, false);
    const [starting, setStarting] = useState(false);
    const [startProblem, setStartProblem] = useState<"offline" | "failed" | null>(null);
    // The calibration read rarely reaches the bridge, so also judge offline from the bridge's last contact.
    const bridge = useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
        refetchInterval: 5 * 60_000,
    });
    const quietFor = minutesSince(bridge.data?.last_seen_at);
    const bridgeQuiet =
        bridge.data?.installed === true && quietFor !== undefined && quietFor >= LOST_CONTACT_AFTER_MINUTES;
    const offline = cal.error instanceof AvimarkBridgeUnreachableError || bridgeQuiet || startProblem === "offline";
    const calFailed = cal.isError && !(cal.error instanceof AvimarkBridgeUnreachableError);
    const loadFailed = calFailed && !cal.data;
    // A failed refetch keeps the cached status on screen, so say it may be out of date.
    const refreshFailed = (calFailed && !!cal.data) || bridge.isError;
    const loading = cal.isLoading && !cal.data;
    const v = describeExamSetup({ state: cal.data, offline });

    const tryAgain = async () => {
        setStarting(true);
        setStartProblem(null);
        try {
            await startAvimarkCalibration();
        } catch (e) {
            // Already running shows as Checking on the next status read. The status read never reaches
            // the bridge, so an unreachable bridge has to be shown from here.
            if (e instanceof AvimarkBridgeUnreachableError) setStartProblem("offline");
            else if (!(e instanceof AvimarkSetupAlreadyRunningError)) {
                reportAvimarkError(e, "exam-setup-try-again");
                setStartProblem("failed");
            }
        } finally {
            setStarting(false);
            void qc.invalidateQueries({ queryKey: avimarkCalibrationKey(accountData?.id) });
        }
    };
    const checkAgain = () => {
        setStartProblem(null);
        void cal.refetch();
        void bridge.refetch();
    };

    const copiedFrom = cal.data?.copiedFrom;
    const copiedDate = formatDateOnly(copiedFrom?.visitDate);
    const copiedLabel =
        copiedFrom?.patientName || copiedDate
            ? `Copied from ${copiedFrom?.patientName ? `${copiedFrom.patientName}'s visit` : "a visit"}${copiedDate ? ` on ${copiedDate}` : ""}`
            : null;

    const action = (() => {
        switch (v.action) {
            case "doesnt-look-right":
                return (
                    <Link className="underline text-max-900 text-sm" to={SETUP_PAGE}>
                        {v.actionLabel}
                    </Link>
                );
            case "pick-exam":
            case "pick-exam-with-doctor":
            case "pick-another-exam":
                return (
                    <Link className="secondary-button" to={SETUP_PAGE}>
                        {v.actionLabel}
                    </Link>
                );
            case "try-again":
                return (
                    <button type="button" className="primary-button" disabled={starting} onClick={tryAgain}>
                        {v.actionLabel}
                    </button>
                );
            case "check-again":
                return (
                    <button type="button" className="secondary-button" onClick={checkAgain}>
                        {v.actionLabel}
                    </button>
                );
            case "contact-support":
                return (
                    <a className="secondary-button" href={SUPPORT_URL}>
                        {v.actionLabel}
                    </a>
                );
            default:
                return null;
        }
    })();

    return (
        <section className="flex flex-col gap-3" aria-labelledby="avimark-exam-setup">
            <h3 id="avimark-exam-setup" className="text-base font-semibold text-gray-900">
                AVImark exam setup
            </h3>
            {loading ? (
                <div className="h-16 animate-pulse rounded-md bg-gray-100" />
            ) : loadFailed ? (
                <p className="text-sm text-gray-700">
                    Couldn't load AVImark exam setup.{" "}
                    <button type="button" className="underline" onClick={() => cal.refetch()}>
                        Try again
                    </button>
                </p>
            ) : (
                <>
                    <div
                        className={classNames(
                            "flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3",
                            TONE[v.tone],
                        )}
                    >
                        <div className="flex min-w-0 flex-1 flex-col">
                            <span className="text-sm font-semibold">{v.title}</span>
                            <span className="text-xs">{v.sentence}</span>
                        </div>
                        {action}
                    </div>
                    {startProblem === "failed" && (
                        <p role="alert" className="text-sm text-red-700">
                            Couldn't start setup. Try again, or contact support if it keeps happening.
                        </p>
                    )}
                    {refreshFailed && (
                        <p className="text-xs text-gray-600">
                            Couldn't refresh this status, so it may be out of date.{" "}
                            <button type="button" className="underline" onClick={checkAgain}>
                                Check again
                            </button>
                        </p>
                    )}
                    {v.key === "done" && cal.data && (
                        <dl className="flex flex-col gap-2 text-sm">
                            {copiedLabel && (
                                <div className="flex justify-between gap-4">
                                    <dt className="text-gray-600">Exam layout</dt>
                                    <dd className="text-gray-900">{copiedLabel}</dd>
                                </div>
                            )}
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Body systems</dt>
                                <dd className="text-gray-900">{cal.data.bodySystemCount} found in your AVImark</dd>
                            </div>
                            {cal.data.run?.status === "done" && cal.data.run.finishedAt && (
                                <div className="flex justify-between gap-4">
                                    <dt className="text-gray-600">Last checked</dt>
                                    <dd className="text-gray-900">
                                        {new Date(cal.data.run.finishedAt).toLocaleDateString()}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    )}
                </>
            )}
        </section>
    );
}
